import { type Cadre, ecrireCadres, LIMITES_ENVOI } from '@admin-pmvb/core'
import { marquerOccupee, rechargementDemande } from './occupation'

// File d'envoi durable des photos (portée de flowAI, src/lib/pwa-outbox.ts).
//
// Tant que les octets d'une photo ne sont pas sur le serveur, seul le téléphone les détient.
// Chaque page est donc écrite dans IndexedDB AVANT l'envoi (elle survit à la fermeture de
// l'appli, au rechargement de mise à jour, au redémarrage du serveur) ; un draineur la renvoie
// jusqu'à ce que le serveur réponde « durable » (octets écrits sur son disque et lignes créées),
// puis l'efface. La reprise est sûre : le serveur dédoublonne par empreinte et par identifiant
// d'envoi (une tranche rejouée ne crée rien de plus).
//
// Les pages d'un même envoi (« Envoyer ») partagent un `envoiId` et partent ensemble, en
// tranches bornées en nombre et en octets ; les champs texte précèdent les fichiers.
// Module sans React, dégradé proprement sans IndexedDB (navigation privée) : listes vides,
// aucune exception.

/** Une page en attente d'envoi confirmé, gardée dans IndexedDB. */
export type ElementFile = {
  /** Clé IndexedDB, unique par page. */
  id: string
  /** Envoi auquel la page appartient (même valeur pour toutes ses pages). */
  envoiId: string
  /** Nombre de pages de l'envoi. */
  nbFichiers: number
  /** Place de la page dans l'ordre choisi. */
  rang: number
  /** Les octets, en pleine résolution : c'est ce qu'il ne faut pas perdre. */
  blob: Blob
  nom: string
  type: string
  taille: number
  creeLe: number
  /** Envois tentés (borne la reprise automatique et l'attente entre essais). */
  tentatives: number
  derniereErreur: string | null
  dernierEssai: number | null
  /** Pas de nouvel essai avant cet instant (réponse « occupé » du serveur). */
  pasAvant?: number | null
  /** Aléa ajouté à l'attente après une erreur (les téléphones ne reviennent pas ensemble). */
  alea?: number
  /** Réponses « occupé » reçues, non comptées comme tentatives jusqu'à un plafond. */
  reportsOccupe?: number
  /** Indication du déposant (« perso »), la même pour toutes les pages de l'envoi. */
  indication?: string
  /** Coins de la page choisis à l'écran : le serveur la redresse ; absent ou null : entière. */
  cadre?: Cadre | null
}

/**
 * Issue d'un envoi : `decouper` = forme de requête refusée (trop de fichiers, corps trop gros
 * pour un intermédiaire), renvoyer en tranches plus petites ; `trop_gros` = fichier au-delà de
 * la limite, écarter lui seul ; `occupe` = serveur saturé, réessayer après son délai.
 */
export type Issue =
  'ok' | 'reessayer' | 'definitif' | 'connexion' | 'decouper' | 'trop_gros' | 'occupe'

export type RaisonAbandon = 'type_refuse' | 'trop_gros' | 'autre'

export type Bilan = {
  /** Pages confirmées « durables » par le serveur (effacées du téléphone). */
  livrees: number
  /** Pages gardées pour plus tard (réseau, serveur, attente). */
  gardees: number
  /** Pages abandonnées sur un refus définitif (format, taille). */
  abandonnees: number
  parRaison: Partial<Record<RaisonAbandon, number>>
  /** Session expirée : l'appelant renvoie vers la connexion. */
  connexionPerdue: boolean
  /** Envois dont au moins une tranche a été confirmée pendant ce passage. */
  envoisLivres: string[]
  /** Serveur occupé : rien ne part avant cet instant. */
  occupeJusqua?: number
  /** Prochain essai automatique de ce qui reste ; absent si rien ne sera réessayé seul. */
  prochainEssai?: number
}

const NOM_BASE = 'pmvb-file-envoi'
const MAGASIN = 'pages'
const VERSION_BASE = 1

/** Au-delà, la reprise automatique s'arrête (la reprise à la main reste possible). */
export const MAX_ESSAIS_AUTO = 6
/** Plafond quand la dernière erreur est passagère (réseau, 5xx) : environ 2 h 30 à 5 min. */
export const MAX_ESSAIS_PASSAGERS = 36
/** Réponses « occupé » non comptées comme tentatives. */
export const MAX_REPORTS_OCCUPE = 15
export const FACTEUR_MAX_OCCUPE = 4
export const ALEA_MAX_MS = 15_000
const OCCUPE_DEFAUT_MS = 30_000
const OCCUPE_MIN_MS = 1_000
const OCCUPE_MAX_MS = 10 * 60_000
const DEBIT_DEFAUT_MS = 60_000
export const ERREUR_OCCUPE = 'occupe'
export const ERREUR_DELAI = 'delai'
const ERREUR_EN_COURS = 'en_cours'
const ERREUR_INTERROMPU = 'interrompu'
/** Raisons d'occupation : requête sur le réseau (non sûre), attente d'un essai (sûre). */
export const RAISON_ENVOI = 'envoi'
export const RAISON_ATTENTE = 'attente-envoi'

/** Erreur passagère (pas la faute du fichier) : réseau, délai, 408, 5xx. */
export function erreurPassagere(e: string | null | undefined): boolean {
  if (e === null || e === undefined || !e.startsWith('HTTP ')) return true
  const m = /^HTTP (\d{3})/.exec(e)
  if (!m) return false
  const statut = Number(m[1])
  return statut === 408 || statut >= 500
}

export function essaisAutoEpuises(el: ElementFile): boolean {
  return (
    el.tentatives >= (erreurPassagere(el.derniereErreur) ? MAX_ESSAIS_PASSAGERS : MAX_ESSAIS_AUTO)
  )
}

export function fileDisponible(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null
  } catch {
    return false
  }
}

function ouvrir(): Promise<IDBDatabase> {
  return new Promise((resoudre, rejeter) => {
    if (!fileDisponible()) {
      rejeter(new Error('IndexedDB indisponible'))
      return
    }
    const r = indexedDB.open(NOM_BASE, VERSION_BASE)
    r.onupgradeneeded = () => {
      if (!r.result.objectStoreNames.contains(MAGASIN))
        r.result.createObjectStore(MAGASIN, { keyPath: 'id' })
    }
    r.onsuccess = () => resoudre(r.result)
    r.onerror = () => rejeter(r.error ?? new Error('ouverture de la file impossible'))
  })
}

/**
 * Une requête dans une transaction. En écriture, résolue seulement à la validation réelle
 * (`oncomplete`) : IndexedDB ne garantit la persistance qu'à ce moment (sinon un abandon de
 * validation, quota ou éviction, perdrait une page déjà retirée de l'écran).
 */
function transaction<T>(
  mode: IDBTransactionMode,
  faire: (m: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return ouvrir().then(
    (db) =>
      new Promise<T>((resoudre, rejeter) => {
        let regle = false
        let resultat: T
        let t: IDBTransaction
        let r: IDBRequest
        try {
          t = db.transaction(MAGASIN, mode)
          r = faire(t.objectStore(MAGASIN))
        } catch (e) {
          db.close()
          rejeter(e instanceof Error ? e : new Error('transaction impossible'))
          return
        }
        r.onsuccess = () => {
          resultat = r.result as T
          if (mode === 'readonly') {
            regle = true
            resoudre(resultat)
          }
        }
        r.onerror = () => {
          regle = true
          rejeter(r.error ?? new Error('requête de la file en échec'))
        }
        t.oncomplete = () => {
          if (!regle) {
            regle = true
            resoudre(resultat)
          }
          db.close()
        }
        t.onabort = () => {
          if (!regle) {
            regle = true
            rejeter(t.error ?? new Error('transaction abandonnée'))
          }
          db.close()
        }
      }),
  )
}

/**
 * Met un envoi entier dans la file, en UNE transaction : un passage du draineur ne le voit
 * jamais à moitié. Tout ou rien ; lève si IndexedDB est indisponible (l'appelant l'annonce).
 */
export async function mettreEnFile(elements: ElementFile[]): Promise<void> {
  if (elements.length === 0) return
  const db = await ouvrir()
  await new Promise<void>((resoudre, rejeter) => {
    let t: IDBTransaction
    try {
      t = db.transaction(MAGASIN, 'readwrite')
      const m = t.objectStore(MAGASIN)
      for (const el of elements) m.put(el)
    } catch (e) {
      db.close()
      rejeter(e instanceof Error ? e : new Error('mise en file impossible'))
      return
    }
    t.oncomplete = () => {
      db.close()
      resoudre()
    }
    t.onerror = () => {
      db.close()
      rejeter(t.error ?? new Error('mise en file en échec'))
    }
    t.onabort = () => {
      db.close()
      rejeter(t.error ?? new Error('mise en file abandonnée'))
    }
  })
}

/** Pages en attente, les plus anciennes d'abord ; [] sans IndexedDB. */
export async function listerFile(): Promise<ElementFile[]> {
  try {
    const lignes = await transaction<ElementFile[]>('readonly', (m) => m.getAll())
    return (lignes ?? []).sort(
      (a, b) => a.creeLe - b.creeLe || a.rang - b.rang || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
    )
  } catch {
    return []
  }
}

export async function compterFile(): Promise<number> {
  try {
    return await transaction<number>('readonly', (m) => m.count())
  } catch {
    return 0
  }
}

export async function retirerDeFile(id: string): Promise<void> {
  try {
    await transaction('readwrite', (m) => m.delete(id))
  } catch {
    /* un retrait raté sera refait au prochain passage (le serveur dédoublonne) */
  }
}

/** Abandonne un envoi entier (choix de la personne). */
export async function abandonnerEnvoi(envoiId: string): Promise<void> {
  for (const el of await listerFile()) if (el.envoiId === envoiId) await retirerDeFile(el.id)
}

/**
 * Lecture puis écriture d'UNE page dans une seule transaction : les transactions en écriture
 * d'un même magasin sont sérialisées, donc deux onglets qui comptent une tentative sur la même
 * page ne perdent jamais un incrément. Faux si la page n'existe plus.
 */
async function modifier(id: string, f: (el: ElementFile) => ElementFile | null): Promise<boolean> {
  let trouve = true
  try {
    const db = await ouvrir()
    await new Promise<void>((resoudre, rejeter) => {
      let t: IDBTransaction
      try {
        t = db.transaction(MAGASIN, 'readwrite')
        const m = t.objectStore(MAGASIN)
        const r = m.get(id)
        r.onsuccess = () => {
          const el = r.result as ElementFile | undefined
          if (!el) {
            trouve = false
            return
          }
          const suivant = f(el)
          if (suivant) m.put(suivant)
        }
      } catch (e) {
        db.close()
        rejeter(e instanceof Error ? e : new Error('modification impossible'))
        return
      }
      t.oncomplete = () => {
        db.close()
        resoudre()
      }
      t.onerror = () => {
        db.close()
        rejeter(t.error ?? new Error('modification en échec'))
      }
      t.onabort = () => {
        db.close()
        rejeter(t.error ?? new Error('modification abandonnée'))
      }
    })
  } catch {
    /* sans IndexedDB : l'appelant garde son instantané */
  }
  return trouve
}

/** Compte une tentative (une seule transaction). */
export async function compterTentative(id: string, erreur: string | null): Promise<void> {
  await modifier(id, (el) => ({
    ...el,
    tentatives: el.tentatives + 1,
    derniereErreur: erreur,
    dernierEssai: Date.now(),
  }))
}

/**
 * La tentative est comptée AVANT le départ de la requête : un envoi coupé (appli tuée,
 * rechargement) ne repasse jamais par le compte après la requête. Faux si la page a été
 * abandonnée depuis : elle ne part pas.
 */
function commencerTentative(id: string, maintenant: number): Promise<boolean> {
  return modifier(id, (el) => ({
    ...el,
    tentatives: el.tentatives + 1,
    derniereErreur: ERREUR_EN_COURS,
    dernierEssai: maintenant,
  }))
}

async function noterErreur(id: string, erreur: string, alea?: number): Promise<void> {
  await modifier(id, (el) => ({
    ...el,
    derniereErreur: erreur,
    ...(alea !== undefined ? { alea } : {}),
  }))
}

/** Annule NOTRE incrément (401, forme de requête refusée) ; celui d'un autre onglet reste. */
async function annulerTentative(avant: ElementFile): Promise<void> {
  await modifier(avant.id, (el) =>
    el.tentatives === avant.tentatives + 1
      ? {
          ...el,
          tentatives: avant.tentatives,
          derniereErreur: avant.derniereErreur,
          dernierEssai: avant.dernierEssai,
        }
      : null,
  )
}

/** Serveur occupé : pas d'essai avant `jusqua`, tentative non comptée jusqu'au plafond. */
async function reporterPourOccupation(avant: ElementFile, jusqua: number): Promise<void> {
  await modifier(avant.id, (el) => {
    const reportsOccupe = (el.reportsOccupe ?? 0) + 1
    const decompter = el.tentatives === avant.tentatives + 1 && reportsOccupe <= MAX_REPORTS_OCCUPE
    return {
      ...el,
      ...(decompter ? { tentatives: avant.tentatives, dernierEssai: avant.dernierEssai } : {}),
      derniereErreur: ERREUR_OCCUPE,
      pasAvant: Math.max(el.pasAvant ?? 0, jusqua),
      reportsOccupe,
    }
  })
}

export function nouvelIdentifiant(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      return crypto.randomUUID()
  } catch {
    /* repli ci-dessous */
  }
  const h = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, '0')
  return `${h()}${h()}-${h()}-4${h().slice(1)}-a${h().slice(1)}-${h()}${h()}${h()}`
}

/**
 * Formulaire d'une tranche : les champs texte d'abord (le serveur les lit avant les octets),
 * puis chaque page sous le champ « fichier ». `rangs` : place de chaque page de la tranche ;
 * `cadres` : coins de chaque page recadrée, dans le même ordre.
 */
export function formulaireTranche(elements: ElementFile[]): FormData {
  const f = new FormData()
  const tete = elements[0]
  if (tete) {
    f.append('envoiId', tete.envoiId)
    f.append('nbFichiers', String(tete.nbFichiers))
    f.append('rangs', elements.map((e) => e.rang).join(','))
    if (tete.indication) f.append('indication', tete.indication)
    const cadres = ecrireCadres(elements.map((e) => e.cadre))
    if (cadres) f.append('cadres', cadres)
  }
  for (const e of elements)
    f.append('fichier', new File([e.blob], e.nom, { type: e.type || 'application/octet-stream' }))
  return f
}

/**
 * Issue d'un envoi selon le statut HTTP et le code de la route. Les octets ne sont effacés que
 * sur une issue vraiment définitive :
 *   - 2xx : ok (après vérification que le corps confirme) ;
 *   - 401 : connexion (session expirée, drainage coupé, page gardée) ;
 *   - 408, 429, 5xx : réessayer ; 503 « occupe » : occupé (délai du serveur, sans tentative) ;
 *   - 413 « trop_gros » : écarter le seul fichier fautif ; 413 autre : découper ;
 *   - 415 « type_refuse » : définitif ; 415 sans ce code (intermédiaire) : réessayer ;
 *   - tout autre 4xx : réessayer (borné par MAX_ESSAIS_AUTO), jamais définitif.
 */
export function classerReponse(statut: number, code?: string | null): Issue {
  if (statut >= 200 && statut < 300) return 'ok'
  if (statut === 401) return 'connexion'
  if (statut === 408 || statut === 429) return 'reessayer'
  if (statut === 503 && code === 'occupe') return 'occupe'
  if (statut >= 500) return 'reessayer'
  if (statut === 413) return code === 'trop_gros' ? 'trop_gros' : 'decouper'
  if (statut === 415) return code === 'type_refuse' ? 'definitif' : 'reessayer'
  return 'reessayer'
}

/** Découpe en tranches consécutives d'au plus `taille` (ordre gardé). */
export function trancher<T>(elements: readonly T[], taille: number): T[][] {
  const pas = Math.max(1, Math.floor(taille))
  const sortie: T[][] = []
  for (let i = 0; i < elements.length; i += pas) sortie.push(elements.slice(i, i + pas))
  return sortie
}

/**
 * Tranches consécutives d'au plus `maxFichiers` éléments ET `maxOctets` octets, remplies dans
 * l'ordre ; un élément plus gros que `maxOctets` part seul.
 */
export function trancherParOctets<T>(
  elements: readonly T[],
  maxFichiers: number,
  maxOctets: number,
  taille: (e: T) => number,
): T[][] {
  const pas = maxFichiers >= 1 ? Math.floor(maxFichiers) : 1
  const budget = maxOctets > 0 ? maxOctets : Number.POSITIVE_INFINITY
  const sortie: T[][] = []
  let courante: T[] = []
  let octets = 0
  for (const e of elements) {
    const brut = taille(e)
    const n = Number.isFinite(brut) && brut > 0 ? brut : 0
    if (courante.length > 0 && (courante.length >= pas || octets + n > budget)) {
      sortie.push(courante)
      courante = []
      octets = 0
    }
    courante.push(e)
    octets += n
  }
  if (courante.length > 0) sortie.push(courante)
  return sortie
}

/** Délai demandé par une réponse « occupé » : corps, puis Retry-After, sinon 30 s ; borné. */
export function delaiOccupe(
  corps: Record<string, unknown> | null,
  retryAfter?: string | null,
): number {
  let ms: number | null = null
  const c = corps?.reessayerDansMs
  if (typeof c === 'number' && Number.isFinite(c)) ms = c
  else if (retryAfter !== null && retryAfter !== undefined && retryAfter.trim() !== '') {
    const s = Number(retryAfter)
    if (Number.isFinite(s)) ms = s * 1000
  }
  if (ms === null) ms = OCCUPE_DEFAUT_MS
  return Math.min(OCCUPE_MAX_MS, Math.max(OCCUPE_MIN_MS, Math.round(ms)))
}

export function tirerAlea(hasard: () => number = Math.random): number {
  let r: number
  try {
    r = hasard()
  } catch {
    r = 0
  }
  return Math.floor((Number.isFinite(r) ? Math.min(Math.max(r, 0), 1) : 0) * ALEA_MAX_MS)
}

/** Attente « occupé » qui grandit avec les refus déjà reçus (× 2^k, au plus × 4). */
export function attenteOccupe(delaiServeur: number, reportsPrecedents: number): number {
  const k = Math.max(0, Math.floor(Number.isFinite(reportsPrecedents) ? reportsPrecedents : 0))
  return Math.min(
    OCCUPE_MAX_MS,
    Math.round(delaiServeur * Math.min(2 ** Math.min(k, 30), FACTEUR_MAX_OCCUPE)),
  )
}

/** Attente avant la N-ième reprise automatique : 2^N s, au plus 5 min. */
export function attenteEntreEssais(tentatives: number): number {
  return Math.min(2 ** tentatives, 300) * 1000
}

function pretAEssayer(el: ElementFile, maintenant: number): boolean {
  if (el.tentatives === 0 || el.dernierEssai === null) return true
  const alea = typeof el.alea === 'number' && Number.isFinite(el.alea) ? Math.max(0, el.alea) : 0
  return maintenant - el.dernierEssai >= attenteEntreEssais(el.tentatives) + alea
}

/** Délai d'une requête : 90 s, plus l'attente à la porte du serveur, plus 8 s par Mo. */
export function delaiRequete(octets: number): number {
  const mo = Math.max(0, Number.isFinite(octets) ? octets : 0) / (1024 * 1024)
  return 90_000 + LIMITES_ENVOI.attentePorteMs + Math.ceil(mo * 8_000)
}

const octetsDe = (el: ElementFile) =>
  typeof el.blob?.size === 'number' && Number.isFinite(el.blob.size) ? el.blob.size : el.taille

/** Prochain essai automatique parmi `elements`, ou null si aucun ne sera réessayé seul. */
export function prochainEssaiAuto(
  elements: readonly ElementFile[],
  occupeJusqua = 0,
): number | null {
  let prochain: number | null = null
  for (const el of elements) {
    if (octetsDe(el) > LIMITES_ENVOI.octetsParFichier || essaisAutoEpuises(el)) continue
    let a = 0
    if (el.tentatives > 0 && el.dernierEssai !== null) {
      const alea =
        typeof el.alea === 'number' && Number.isFinite(el.alea) ? Math.max(0, el.alea) : 0
      a = el.dernierEssai + attenteEntreEssais(el.tentatives) + alea
    }
    if (typeof el.pasAvant === 'number' && Number.isFinite(el.pasAvant))
      a = Math.max(a, el.pasAvant)
    a = Math.max(a, occupeJusqua)
    prochain = prochain === null ? a : Math.min(prochain, a)
  }
  return prochain
}

class CoupureRequete extends Error {}

/** Requête et lecture du corps sous le délai, combinées au signal de l'appelant. */
async function requeteAvecDelai<T>(
  f: typeof fetch,
  url: string,
  init: RequestInit,
  delaiMs: number,
  externe: AbortSignal | undefined,
  lire: (r: Response) => Promise<T>,
): Promise<{ r: Response; corps: T }> {
  const controle = new AbortController()
  let rejeterDelai: (e: Error) => void = () => {}
  const delai = new Promise<never>((_, rej) => {
    rejeterDelai = rej
  })
  const couper = (m: string) => {
    rejeterDelai(new CoupureRequete(m))
    controle.abort()
  }
  const minuteur = setTimeout(() => couper(ERREUR_DELAI), delaiMs)
  const surAbandon = () => couper(ERREUR_INTERROMPU)
  if (externe?.aborted) surAbandon()
  else externe?.addEventListener('abort', surAbandon, { once: true })
  try {
    const travail = (async () => {
      const r = await f(url, { ...init, signal: controle.signal })
      return { r, corps: await lire(r) }
    })()
    return await Promise.race([travail, delai])
  } finally {
    clearTimeout(minuteur)
    externe?.removeEventListener('abort', surAbandon)
  }
}

async function lireJson(r: Response): Promise<Record<string, unknown> | null> {
  try {
    const c = (await r.clone().json()) as unknown
    return c !== null && typeof c === 'object' && !Array.isArray(c)
      ? (c as Record<string, unknown>)
      : null
  } catch {
    return null
  }
}

/** Un 2xx qui n'est pas la réponse de la route (page d'un intermédiaire) ne prouve rien. */
function confirme(corps: Record<string, unknown> | null): boolean {
  return corps?.statut === 'ok' && corps.durable === true
}

/** Taille de tranche abaissée après un délai dépassé (cet onglet seulement). */
const taillesReduites = new Map<string, number>()
const envoisEnCours = new Map<string, number>()

/** Une requête portant cet envoi est-elle sur le réseau ? (on ne rappelle pas des octets partis) */
export function envoiEnCours(envoiId: string): boolean {
  return (envoisEnCours.get(envoiId) ?? 0) > 0
}

function marquerEnCours(cle: string, oui: boolean) {
  const n = (envoisEnCours.get(cle) ?? 0) + (oui ? 1 : -1)
  if (n > 0) envoisEnCours.set(cle, n)
  else envoisEnCours.delete(cle)
}

let chaine: Promise<unknown> = Promise.resolve()
let passageDeFondEnAttente: Promise<Bilan> | null = null
const envoisPrioritaires = new Set<string>()
let occupeJusqua = 0
let connexionBloqueeJusqua = 0
const BLOCAGE_CONNEXION_MS = 5 * 60_000
const URL_ENVOI = '/api/envois'

export function reinitialiserFilePourTests(): void {
  occupeJusqua = 0
  connexionBloqueeJusqua = 0
  taillesReduites.clear()
}

export type OptionsDrainage = {
  /** Reprise à la main : ignore l'attente et le plafond d'essais. */
  force?: boolean
  /** Envoi à passer en premier (un envoi neuf n'attend pas derrière un arriéré). */
  prioritaire?: string
  fetch?: typeof fetch
  maintenant?: number
  signal?: AbortSignal
  hasard?: () => number
}

/**
 * Vide la file : renvoie chaque page en attente. Passages sérialisés (un à la fois par onglet) :
 * un appel concurrent passe APRÈS celui en cours ; les passages de fond identiques sont fusionnés.
 * Chaque envoi part en tranches d'au plus 20 pages et 100 Mo. Une réponse « occupé » arrête le
 * passage et retient tous les suivants de l'onglet jusqu'au délai du serveur, sans compter de
 * tentative. Ne lève jamais.
 */
export function drainerFile(o: OptionsDrainage = {}): Promise<Bilan> {
  if (o.prioritaire) envoisPrioritaires.add(o.prioritaire)
  const deFond = !o.force && !o.prioritaire
  if (deFond && passageDeFondEnAttente) return passageDeFondEnAttente
  const suivant: Promise<Bilan> = chaine
    .catch(() => {})
    .then(() => {
      if (passageDeFondEnAttente === suivant) passageDeFondEnAttente = null
      return passer(o)
    })
    .finally(() => {
      if (o.prioritaire) envoisPrioritaires.delete(o.prioritaire)
    })
  if (deFond) passageDeFondEnAttente = suivant
  chaine = suivant.catch(() => {})
  return suivant
}

async function passer(o: OptionsDrainage): Promise<Bilan> {
  const bilan = await passerElements(o)
  if (!bilan.connexionPerdue) {
    const p = prochainEssaiAuto(await listerFile(), occupeJusqua)
    if (p !== null) bilan.prochainEssai = p
  }
  return bilan
}

type Tranche = { cle: string; elements: ElementFile[] }

async function passerElements(o: OptionsDrainage): Promise<Bilan> {
  const bilan: Bilan = {
    livrees: 0,
    gardees: 0,
    abandonnees: 0,
    parRaison: {},
    connexionPerdue: false,
    envoisLivres: [],
  }
  const abandonner = async (els: ElementFile[], raison: RaisonAbandon) => {
    for (const el of els) await retirerDeFile(el.id)
    bilan.abandonnees += els.length
    bilan.parRaison[raison] = (bilan.parRaison[raison] ?? 0) + els.length
  }
  const f = o.fetch ?? fetch
  const maintenant = o.maintenant ?? Date.now()
  if (o.force) connexionBloqueeJusqua = 0
  else if (maintenant < connexionBloqueeJusqua) {
    bilan.connexionPerdue = true
    bilan.gardees = (await listerFile()).length
    return bilan
  }
  const horloge = () => o.maintenant ?? Date.now()
  const alea = () => tirerAlea(o.hasard)
  const elements = await listerFile()

  const groupes = new Map<string, ElementFile[]>()
  for (const el of elements) {
    const g = groupes.get(el.envoiId)
    if (g) g.push(el)
    else groupes.set(el.envoiId, [el])
  }
  const prioritaires = [...envoisPrioritaires].reverse()
  const rang = (k: string) => {
    const i = prioritaires.indexOf(k)
    return i < 0 ? prioritaires.length : i
  }
  const ordonnes = [...groupes.entries()].sort(([a], [b]) => rang(a) - rang(b))
  const prioritesVues = new Set(envoisPrioritaires)

  const file: Tranche[] = []
  for (const [cle, groupe] of ordonnes) {
    const prets: ElementFile[] = []
    const tropGros: ElementFile[] = []
    for (const el of groupe) {
      if (octetsDe(el) > LIMITES_ENVOI.octetsParFichier) {
        tropGros.push(el)
        continue
      }
      if (typeof el.pasAvant === 'number' && maintenant < el.pasAvant) {
        bilan.gardees++
        bilan.occupeJusqua = Math.max(bilan.occupeJusqua ?? 0, el.pasAvant)
        continue
      }
      const force = !!o.force && (!o.prioritaire || cle === o.prioritaire)
      if (!force && (essaisAutoEpuises(el) || !pretAEssayer(el, maintenant))) {
        bilan.gardees++
        continue
      }
      prets.push(el)
    }
    if (tropGros.length > 0) await abandonner(tropGros, 'trop_gros')
    const taille = Math.min(LIMITES_ENVOI.fichiersParRequete, taillesReduites.get(cle) ?? Infinity)
    for (const t of trancherParOctets(prets, taille, LIMITES_ENVOI.octetsParRequete, octetsDe))
      file.push({ cle, elements: t })
  }

  // Renvoie tout de suite en tranches plus petites, sans compter la tentative (la forme de la
  // requête était refusée, pas un échec passager).
  const redecouper = async (t: Tranche, taille: number) => {
    for (const el of t.elements) await annulerTentative(el)
    file.unshift(...trancher(t.elements, taille).map((elements) => ({ cle: t.cle, elements })))
  }

  let debitDepasse = false
  for (let t = file.shift(); t; t = file.shift()) {
    let n = t.elements.length
    const reduite = taillesReduites.get(t.cle)
    if (reduite !== undefined && n > reduite) {
      const cle = t.cle
      file.unshift(...trancher(t.elements, reduite).map((elements) => ({ cle, elements })))
      continue
    }
    const ceder = [...envoisPrioritaires].some((k) => !prioritesVues.has(k))
    const occupe = horloge() < occupeJusqua
    if (occupe) bilan.occupeJusqua = Math.max(bilan.occupeJusqua ?? 0, occupeJusqua)
    if (
      bilan.connexionPerdue ||
      debitDepasse ||
      occupe ||
      ceder ||
      o.signal?.aborted ||
      rechargementDemande()
    ) {
      bilan.gardees += n
      continue
    }
    const vivants: ElementFile[] = []
    for (const el of t.elements) if (await commencerTentative(el.id, horloge())) vivants.push(el)
    if (vivants.length === 0) continue
    if (vivants.length < n) {
      t.elements = vivants
      n = vivants.length
    }
    let r: Response
    let corps: Record<string, unknown> | null
    marquerOccupee(RAISON_ENVOI, true)
    marquerEnCours(t.cle, true)
    try {
      const octets = t.elements.reduce((s, el) => s + octetsDe(el), 0)
      ;({ r, corps } = await requeteAvecDelai(
        f,
        URL_ENVOI,
        { method: 'POST', credentials: 'same-origin', body: formulaireTranche(t.elements) },
        delaiRequete(octets),
        o.signal,
        lireJson,
      ))
    } catch (e) {
      // Réseau, délai, abandon : on GARDE (tentative déjà comptée), jamais d'effacement.
      const m = e instanceof Error ? e.message : String(e)
      // Délai dépassé sur plusieurs pages : le lien est trop lent pour cette taille ; les
      // requêtes suivantes de cet envoi partent par moitiés.
      if (m === ERREUR_DELAI && n > 1) taillesReduites.set(t.cle, Math.ceil(n / 2))
      // Un seul tirage par tranche : les pages d'un envoi restent groupées à la reprise.
      const a = alea()
      for (const el of t.elements) await noterErreur(el.id, m, a)
      bilan.gardees += n
      continue
    } finally {
      marquerEnCours(t.cle, false)
      marquerOccupee(RAISON_ENVOI, false)
    }
    const code = !r.ok && typeof corps?.code === 'string' ? corps.code : null
    const issue = classerReponse(r.status, code)

    if (issue === 'ok') {
      if (!confirme(corps)) {
        for (const el of t.elements) await noterErreur(el.id, `HTTP ${r.status} non confirmé`)
        bilan.gardees += n
        continue
      }
      for (const el of t.elements) await retirerDeFile(el.id)
      if (!bilan.envoisLivres.includes(t.cle)) bilan.envoisLivres.push(t.cle)
      const ecartes = Math.min(
        Math.max(typeof corps?.ecartes === 'number' ? Math.floor(corps.ecartes) : 0, 0),
        n,
      )
      bilan.livrees += n - ecartes
      if (ecartes > 0) {
        bilan.abandonnees += ecartes
        bilan.parRaison.type_refuse = (bilan.parRaison.type_refuse ?? 0) + ecartes
      }
    } else if (issue === 'connexion') {
      for (const el of t.elements) await annulerTentative(el)
      bilan.connexionPerdue = true
      connexionBloqueeJusqua = horloge() + BLOCAGE_CONNEXION_MS
      bilan.gardees += n
    } else if (issue === 'decouper') {
      if (n > 1) {
        const max = typeof corps?.maxFichiers === 'number' ? Math.floor(corps.maxFichiers) : 0
        const moitie = Math.ceil(n / 2)
        await redecouper(t, max >= 1 && max < moitie ? max : moitie)
      } else {
        for (const el of t.elements) await noterErreur(el.id, `HTTP ${r.status}`)
        bilan.gardees += n
      }
    } else if (issue === 'trop_gros') {
      const max =
        typeof corps?.maxOctets === 'number' && corps.maxOctets > 0
          ? corps.maxOctets
          : LIMITES_ENVOI.octetsParFichier
      let fautifs = t.elements.filter((el) => octetsDe(el) > max)
      if (fautifs.length === 0 && n === 1) fautifs = t.elements
      if (fautifs.length === 0) {
        await redecouper(t, Math.ceil(n / 2))
        continue
      }
      await abandonner(fautifs, 'trop_gros')
      const reste = t.elements.filter((el) => !fautifs.includes(el))
      if (reste.length > 0) {
        for (const el of reste) await annulerTentative(el)
        file.unshift({ cle: t.cle, elements: reste })
      }
    } else if (issue === 'definitif') {
      await abandonner(t.elements, r.status === 415 ? 'type_refuse' : 'autre')
    } else if (issue === 'occupe') {
      const precedents = Math.max(0, ...t.elements.map((el) => el.reportsOccupe ?? 0))
      const jusqua =
        horloge() +
        attenteOccupe(delaiOccupe(corps, r.headers.get('retry-after')), precedents) +
        alea()
      occupeJusqua = Math.max(occupeJusqua, jusqua)
      bilan.occupeJusqua = Math.max(bilan.occupeJusqua ?? 0, occupeJusqua)
      for (const el of t.elements) await reporterPourOccupation(el, jusqua)
      bilan.gardees += n
    } else if (r.status === 429) {
      // Débit limité : pas la faute des pages ; attendre la fenêtre de la route.
      const indice =
        typeof corps?.reessayerDansMs === 'number' || r.headers.get('retry-after') !== null
      const attente = indice ? delaiOccupe(corps, r.headers.get('retry-after')) : DEBIT_DEFAUT_MS
      const jusqua = horloge() + attente + alea()
      occupeJusqua = Math.max(occupeJusqua, jusqua)
      bilan.occupeJusqua = Math.max(bilan.occupeJusqua ?? 0, occupeJusqua)
      for (const el of t.elements) await reporterPourOccupation(el, jusqua)
      debitDepasse = true
      bilan.gardees += n
    } else {
      const a = alea()
      for (const el of t.elements)
        await noterErreur(el.id, `HTTP ${r.status}${code ? ` ${code}` : ''}`, a)
      bilan.gardees += n
    }
  }
  return bilan
}
